import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

function toInt(v, fallback = 1) {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
}

function snapNearest(value, min, step) {
  let v = toInt(value, min);
  if (v < min) return min;

  const offset = v - min;
  const stepIndex = Math.round(offset / step);
  const snapped = min + stepIndex * step;

  return snapped < min ? min : snapped;
}

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but:
 * - uses absolute max limits (cart total) (Horizon 3.2.1 behaviour)
 * - respects Intaprint min/step rules via data-min/data-step
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  // In cart we want to use data-min/data-step (variant-aware).
  getCurrentValues() {
    const base = super.getCurrentValues?.() || {};
    const input = this.refs?.quantityInput;

    const dataMin = input?.dataset?.min;
    const dataStep = input?.dataset?.step;

    const min = Math.max(1, toInt(dataMin ?? base.min, 1));
    const step = Math.max(1, toInt(dataStep ?? base.step, 1));

    // Ensure value is int and never below min when present
    const rawValue = toInt(input?.value ?? base.value, min);
    const value = Math.max(min, rawValue);

    return { ...base, min, step, value };
  }

  /**
   * Cart page: uses absolute max (how much can be in cart total)
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  increaseQuantity(evt) {
    evt?.preventDefault?.();

    const { value, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    let next = value + step;
    if (effectiveMax !== null) next = Math.min(next, effectiveMax);

    this.refs.quantityInput.value = String(next);

    this.refs.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    const changeEvt = new Event('change', { bubbles: true });
    this.refs.quantityInput.dispatchEvent(changeEvt);

    return super.setQuantity(changeEvt);
  }

  decreaseQuantity(evt) {
    evt?.preventDefault?.();

    const { value, step, min } = this.getCurrentValues();

    let next = value - step;
    next = Math.max(next, min);

    this.refs.quantityInput.value = String(next);

    this.refs.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    const changeEvt = new Event('change', { bubbles: true });
    this.refs.quantityInput.dispatchEvent(changeEvt);

    return super.setQuantity(changeEvt);
  }

  // Snap typed values (or any setQuantity call) to min/step rules
  setQuantity(evt) {
    const { min, step, value } = this.getCurrentValues();

    let next = value;
    if (step > 1) next = snapNearest(next, min, step);
    else next = Math.max(next, min);

    this.refs.quantityInput.value = String(next);

    this.refs.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.refs.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    return super.setQuantity(evt);
  }

  /**
   * Cart buttons are always managed client-side, never server-disabled
   * Also respects step when checking max
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value + step > effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
